import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';
import { UserAccountDTO, UserLoginDataDTO } from '../classes/user-account.dto';
import { StorageService } from './storage.service';
import { DataService } from './data.service';
import { AppointmentStepperService } from './appointment-stepper.service';
import { TranslateService } from '@ngx-translate/core';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';
import { ModalHandleLoginErrorsComponent } from '../components/modals/modal-handle-login-errors/modal-handle-login-errors.component';
import { KeycloakService } from 'keycloak-angular';
import { jwtDecode } from 'jwt-decode';
import { ConfigService } from './env-config.service';
import { GtmService } from './gtm.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  disabledMFA?: string;

  constructor(
    private httpService: HttpService,
    private storageService: StorageService,
    private dataService: DataService,
    private appointmentStepperService: AppointmentStepperService,
    private translateService: TranslateService,
    private router: Router,
    private modal: NzModalService,
    public keycloak: KeycloakService,
    private configService: ConfigService,
    private gtmService: GtmService
  ) {
    this.isAuthenticatedSubject.next(!!this.storageService.getItem('authToken'));
    this.configService.getConfig().subscribe((res: any) =>{
      this.disabledMFA  = res?.disabledMFA;
    });
    }

  public login(loginData: UserLoginDataDTO): Observable<UserAccountDTO> {
    return this.httpService.login(loginData);
  }

  public async logout(): Promise<void> {    
    this.storageService.removeItem('authToken');
    this.storageService.removeItem('userData');
    this.storageService.removeItem('oldUserData');
    this.storageService.removeItem('bodyAppointment');
    this.storageService.removeItem('bodyUserLog');
    this.storageService.removeItem('bodyApiNotification');
    this.storageService.removeItem('consultType');
    this.storageService.removeItem('updateProfileFrom');
    this.appointmentStepperService.clearUsers();
    this.isAuthenticatedSubject.next(false);
    this.dataService.resetData();
    if (!this.disabledMFA) {
      try {
        this.keycloak.logout(window.location.origin + "/logout");
        this.dataService.resetData();
        return;
      } catch (err) {
        console.error('Errore nel logout da Keycloak', err);
      }
    } else {
      if (this.storageService.getItem('OnMobile') === 'true') {   
        this.storageService.removeItem('OnMobile');         
        window.location.href = '/logout';
      } 
    }
    this.router.navigate(['/login']);
  }

  public loginWithKeycloak(): Promise<void> {
    return this.keycloak.login({
      redirectUri: window.location.origin + '/home'
    });
  }

  public isSSOLogin(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decoded: any = jwtDecode(token)
      return decoded?.login_type !== 'myclinic';
    } catch (e) {
      console.error('Errore nel decoding del token', e);
      return false;
    }
  }

  public getAuthToken(): object | null {
    return JSON.parse(this.storageService.getItem('authToken') || 'null');
  }

  public isAuthenticated(): boolean {
    return !!this.storageService.getItem('authToken');
  }

  public isAuthenticatedObservable(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  //TODO: CHECK IF THAT WORK
  public checkLoginStatus(response: any, from?: 'activation' | 'login', navigate: boolean = true): boolean {
    if (response.isAccountDisabledFromMaster) {
      const textsModal = this.translateService.instant('SPA_dialogs.DisabledAccountPopup');
      this.openModalError(textsModal, 'download', response);
      return false;
    } else if (response.isExpired && response.canDownloadMedicalData) {
      const textsModal = this.translateService.instant('SPA_dialogs.expiredAccountWithLink');
      this.openModalError(textsModal, 'download', response);
      return false;
    } else if (response.isExpired && response.status === 'Purchase') {
      if ((response.namePolicy !== null && response.namePolicy === 'MY CLINIC UNLIMITED MENSILE') || response.namePolicy === 'MY CLINIC CLASSIC MENSILE') {
        const textsModal = this.translateService.instant('SPA_dialogs.expiredAccountPurchaseMonthly');
        this.openModalError(textsModal, 'notallowed');
      } else if (response.namePolicy === null) {
        const textsModal = this.translateService.instant('SPA_dialogs.expiredAccountPurchase');
        this.openModalError(textsModal, 'notallowed');
      }
      return false;
    } else if (response.isExpired) {
      const textsModal = this.translateService.instant('SPA_dialogs.expiredAccount');
      this.openModalError(textsModal, 'notallowed');
      return false;
    } else if (!response.canStillUseMyClinic) {
      localStorage.removeItem("authToken");
      const textsModal = this.translateService.instant('SPA_dialogs.canStillUseMyClinic');
      this.openModalError(textsModal, 'notallowed');
      this.router.navigate(['/landing-page']).then();
      return false;
    } else if (response.profileNeedsToBeUpdated) {
      this.storageService.setItem('userDataToBeUpdated', response);
      const textsModal = this.translateService.instant('SPA_dialogs.updateProfileNeeded');
      this.openModalError(textsModal, 'update', response, from);
      return false;
    } else if (response.canStillUseMyClinic) {
      this.storageService.setItem('authToken', response.token);
      this.storageService.setItem('userData', response);
      this.isAuthenticatedSubject.next(true);
      if (navigate) {
        this.router.navigate(['/home']).then();
      }

       Promise.resolve().then(() => {
        try {
          this.gtmService.pushUserData({
            id: response.individualId,
            email: response.email,
            birthdate: response.birthDate,
            gender: response.gender,
            creationDate: response.activationDate,
            plan: response.namePolicy,
            consults: response.consultServiceAllowed,
            requestsCareManager: response.careManagerServiceAllowed,
            phone: response.phoneNumber,
            firstName: response.name,
            lastName: response.surname,
            street: response.address,
            city: response.city,
            region: response.district,
            country: response.country,
            postalCode: response.postalCode,
          });
        } catch (e) {
          console.warn('GTM pushUserData skipped', e);
        }
      });
      return true;
    }

    return false;
  }

  private openModalError(text: any, action?: string, response?: any, from?: 'activation' | 'login') {
    const isUpdate = action === 'update';
    const modalRef: NzModalRef<ModalHandleLoginErrorsComponent> = this.modal.create({
      nzContent: ModalHandleLoginErrorsComponent,
      nzClosable: !isUpdate,
      nzMaskClosable: !isUpdate,
      nzKeyboard: !isUpdate,
      nzFooter: null,
      nzData: {
        dataModal: {
          texts: { title: text.title, content: text.content, button: text.button },
          hasTwoButtons: action === 'download',
          actionButton: () => {
            switch (action) {
              case 'update':
                this.storageService.setItem('authToken', response.token);
                if(from){
                  this.storageService.setItem('updateProfileFrom', from);
                }
                this.router.navigate(['/login/update-profile'], {
                  state: { from }
                });
                modalRef.close();
                break;
              case 'download':
                this.storageService.setItem('authToken', response.token);
                this.httpService.downloadMedicalData(response.individualId).subscribe({
                  next: (value) => {
                    if (value) {
                      const link = document.createElement('a');
                      link.href = value.url;
                      link.click();
                    }
                  },
                });
                this.storageService.removeItem('authToken');
                modalRef.close();
                break;
              case 'notallowed':
                this.storageService.removeItem('authToken');
                this.keycloak.logout();
                modalRef.close();
                break;
              default:
                modalRef.close();
                break;
            }
          },
        },
      },
    });
  }
}
